import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Seo } from 'src/components/seo';
import SvgIcon from '@mui/material/SvgIcon';
import SearchMdIcon from '@untitled-ui/icons-react/build/esm/SearchMd';
import XIcon from '@untitled-ui/icons-react/build/esm/X';
import ShoppingBag03Icon from '@untitled-ui/icons-react/build/esm/ShoppingBag03';
import { PricingPlan } from 'src/sections/pricing/pricing-plan';
import { DataHelper } from 'src/services/dataHelper';
import { useNavigate } from 'react-router-dom';
import { paths } from 'src/paths';
import Loader from 'src/pages/components/loader';
import { useTranslation } from 'react-i18next';
import { useCart } from 'src/hooks/use-carts';

const TOP_NAV_HEIGHT = 64;

const Page = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [searching, setSearching] = useState('');
  const [searchData, setSearchData] = useState([]);
  const containerRef = useRef(null);
  const pageRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const t = (en, ar) => (currentLang === 'ar' ? ar : en);
  const { refreshCartCount } = useCart();

  const fetchData = async (pageNumber, pageSize) => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await new DataHelper().getListPos('Items', pageSize, pageNumber + 1);
      const newRows = res?.data?.list || [];
      if (newRows.length > 0) {
        setRows((prev) => [...prev, ...newRows]);
        setSearchData((prev) => [...prev, ...newRows]);
        const more = newRows.length === pageSize;
        hasMoreRef.current = more;
        setHasMore(more);
      } else {
        hasMoreRef.current = false;
        setHasMore(false);
      }
    } catch (error) {
      console.error(error);
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      loadingRef.current = false;
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const loadNextPage = useCallback(() => {
    if (loadingRef.current || !hasMoreRef.current) return;
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    setPage(nextPage);
    fetchData(nextPage, rowsPerPage);
  }, [rowsPerPage]);

  useEffect(() => {
    fetchData(page, rowsPerPage);
    refreshCartCount();
  }, []);

  const handleScroll = useCallback(() => {
    if (loadingRef.current || !hasMoreRef.current) return;

    if (isMobile) {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      if (scrollTop + clientHeight >= scrollHeight - 120) {
        loadNextPage();
      }
      return;
    }

    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      loadNextPage();
    }
  }, [isMobile, loadNextPage]);

  useEffect(() => {
    const target = isMobile ? window : containerRef.current;
    if (!target) return undefined;
    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => target.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isMobile]);

  const filterFunction = (text) => {
    const filter = searchData.filter((item) =>
      item.itemName?.toLowerCase().includes(text.toLowerCase()) ||
      item.categoryName?.toLowerCase().includes(text.toLowerCase()) ||
      item.unitName?.toLowerCase().includes(text.toLowerCase()) ||
      item.code === text
    );
    setRows(filter);
    setSearching(text);
  };

  const onPress = (id) => {
    navigate(paths.inv.product.createProduct, { state: { ID: id } });
  };

  const visibleProducts = useMemo(
    () => rows.filter((row) => row.isDisplay),
    [rows]
  );

  const handleClearSearch = () => {
    setSearching('');
    setRows(searchData);
  };

  const itemCountLabel = visibleProducts.length === 1
    ? t('1 item', 'منتج واحد')
    : t(`${visibleProducts.length} items`, `${visibleProducts.length} منتج`);

  return (
    <>
      <Seo title={t('Products', 'المنتجات')} />

      <Box
        component="main"
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          height: { xs: 'auto', md: `calc(100vh - ${TOP_NAV_HEIGHT}px)` },
          maxWidth: '100%',
          minHeight: { xs: 'auto', md: `calc(100vh - ${TOP_NAV_HEIGHT}px)` },
          overflow: { xs: 'visible', md: 'hidden' },
          overflowX: 'hidden',
          px: { xs: 1, sm: 2, md: 2.5 },
          pt: { xs: 1, sm: 1.5 },
          pb: { xs: 2.5, sm: 4 },
          width: '100%'
        }}
      >
        {/* Header + search toolbar */}
        <Box
          sx={{
            flexShrink: 0,
            mb: { xs: 1.25, sm: 2 },
            width: '100%'
          }}
        >
          <Stack spacing={{ xs: 1.25, sm: 2 }}>
            <Stack
              alignItems="flex-start"
              direction="row"
              justifyContent="space-between"
              spacing={1}
              sx={{ width: '100%' }}
            >
              <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                    fontWeight: 700,
                    lineHeight: 1.2,
                    m: 0
                  }}
                  variant="h6"
                >
                  {t('Products', 'المنتجات')}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    lineHeight: 1.4,
                    mt: 0.35,
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2
                  }}
                  variant="body2"
                >
                  {t('Browse and order from our catalog', 'تصفح واطلب من كتالوجنا')}
                </Typography>
              </Box>
              <Chip
                icon={(
                  <SvgIcon fontSize="small">
                    <ShoppingBag03Icon />
                  </SvgIcon>
                )}
                label={itemCountLabel}
                sx={{
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.50',
                  border: 'none',
                  flexShrink: 0,
                  fontSize: { xs: '0.6875rem', sm: '0.8125rem' },
                  fontWeight: 600,
                  height: { xs: 26, sm: 28 },
                  maxWidth: { xs: '42%', sm: 'none' },
                  mt: 0.125,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    px: { xs: 0.75, sm: 1.25 },
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
                variant="outlined"
              />
            </Stack>

            <TextField
              fullWidth
              onChange={(e) => filterFunction(e.target.value)}
              placeholder={
                isSmallMobile
                  ? t('Search products...', 'ابحث عن منتجات...')
                  : t('Search products, codes, categories...', 'ابحث عن منتجات، أكواد، فئات...')
              }
              size="small"
              value={searching}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon color="action" fontSize="small">
                      <SearchMdIcon />
                    </SvgIcon>
                  </InputAdornment>
                ),
                endAdornment: searching ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t('Clear search', 'مسح البحث')}
                      edge="end"
                      onClick={handleClearSearch}
                      size="small"
                      sx={{ mr: -0.5 }}
                    >
                      <SvgIcon fontSize="small">
                        <XIcon />
                      </SvgIcon>
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  minHeight: { xs: 40, sm: 42 },
                  '& input': {
                    py: { xs: 1, sm: 1.25 }
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }}
              variant="outlined"
            />
          </Stack>
        </Box>

        {/* Product grid area */}
        <Box
          ref={containerRef}
          sx={{
            flex: { xs: 'none', md: 1 },
            maxHeight: { xs: 'none', md: '100%' },
            minHeight: { xs: 280, md: 0 },
            overflowX: 'hidden',
            overflowY: { xs: 'visible', md: 'auto' },
            width: '100%',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'action.disabled',
              borderRadius: 3
            }
          }}
        >
          {initialLoading && visibleProducts.length === 0 ? (
            <Loader fullScreen message={t('', '')} />
          ) : visibleProducts.length === 0 && !loading ? (
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: 'background.paper',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: { xs: 2, sm: 3 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                py: { xs: 5, sm: 8 },
                px: { xs: 1.5, sm: 2 }
              }}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.50',
                  borderRadius: '50%',
                  display: 'flex',
                  height: { xs: 56, sm: 72 },
                  justifyContent: 'center',
                  mb: { xs: 1.5, sm: 2 },
                  width: { xs: 56, sm: 72 }
                }}
              >
                <SvgIcon color="disabled" sx={{ fontSize: { xs: 28, sm: 36 } }}>
                  <ShoppingBag03Icon />
                </SvgIcon>
              </Box>
              <Typography sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, fontWeight: 700, mb: 0.5 }} variant="h6">
                {t('No products found', 'لا توجد منتجات')}
              </Typography>
              <Typography
                align="center"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, maxWidth: 320, mb: 2, px: 1 }}
                variant="body2"
              >
                {searching
                  ? t('Try a different search term', 'جرّب كلمة بحث مختلفة')
                  : t('Check back soon for new arrivals', 'عد قريباً للاطلاع على وصولات جديدة')}
              </Typography>
              {searching && (
                <Chip
                  clickable
                  label={t('Clear search', 'مسح البحث')}
                  onClick={handleClearSearch}
                  sx={{ fontWeight: 600 }}
                  variant="outlined"
                />
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: { xs: 1, sm: 1.5, md: 2 },
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                  lg: 'repeat(4, minmax(0, 1fr))',
                  xl: 'repeat(5, minmax(0, 1fr))'
                },
                pb: { xs: 1, sm: 0 },
                width: '100%'
              }}
            >
              {visibleProducts.map((row) => (
                <Box key={row.ID} sx={{ minWidth: 0 }}>
                  <PricingPlan
                    code={row.code}
                    cta={t('View', 'عرض')}
                    currency={t('AED', 'درهم')}
                    description={row.remarks || 'Description.'}
                    image={row.image}
                    name={row.itemName}
                    popular
                    price={row.salePrice?.toFixed(2)}
                    onClick={() => onPress(row.ID)}
                  />
                </Box>
              ))}
            </Box>
          )}

          {loading && !initialLoading && (
            <Box sx={{ py: { xs: 2, sm: 3 } }}>
              <Loader fullScreen={false} message={t('Loading more...', 'جاري تحميل المزيد...')} />
            </Box>
          )}

          {!hasMore && visibleProducts.length > 0 && !loading && (
            <Typography
              align="center"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, py: { xs: 2, sm: 2.5 } }}
              variant="body2"
            >
              {t("You've reached the end of the catalog", 'لقد وصلت إلى نهاية الكتالوج')}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Page;
